import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

type Dashboard = {
  slug: string;
  name: string;
  description: string;
  embedUrl: string;
  src: SafeResourceUrl;
};

@Component({
  selector: 'app-admin-dashboards',
  templateUrl: './admin-dashboards.component.html',
  styleUrl: './admin-dashboards.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminDashboardsComponent implements OnInit {
  constructor(
    private DomSanitizer: DomSanitizer,
    private ActivatedRoute: ActivatedRoute,
    private Router: Router,
    private ChangeDetectorRef: ChangeDetectorRef
  ) {}

  currentDashboardSlug?: string;
  fullscreenDashboard?: Dashboard;
  selectedDashboard?: Dashboard;

  dashboards: Dashboard[] = [
    this.createDashboard({
      slug: 'executive-financial-summary',
      name: 'Executive Financial Summary',
      description:
        'High-level financial metrics and trendlines for executive review.',
      embedUrl:
        'https://us-east-1.quicksight.aws.amazon.com/sn/embed/share/accounts/861551277616/dashboards/d08b08df-de8a-4481-8731-744560a71e2b?directory_alias=vive'
    }),
    this.createDashboard({
      slug: 'payment-detail-report',
      name: 'Payment Detail Report',
      description:
        'Transaction-level payment detail for finance and operations analysis.',
      embedUrl:
        'https://us-east-1.quicksight.aws.amazon.com/sn/embed/share/accounts/861551277616/dashboards/b415d91c-4972-4d90-8b72-3f44b312fd40?directory_alias=vive'
    }),
    this.createDashboard({
      slug: 'registration-status',
      name: 'Registration Status',
      description: 'Registration status metrics and trends.',
      embedUrl:
        'https://us-east-1.quicksight.aws.amazon.com/sn/embed/share/accounts/861551277616/dashboards/fd8b11c0-5357-4155-8984-6988919d07af?directory_alias=vive'
    }),
    this.createDashboard({
      slug: 'client-health',
      name: 'Client Health',
      description: 'Client health metrics and trends.',
      embedUrl:
        'https://us-east-1.quicksight.aws.amazon.com/sn/embed/share/accounts/861551277616/dashboards/fe5b7a34-2d4a-4030-8815-61f3538ded59?directory_alias=vive'
    }),
    this.createDashboard({
      slug: 'operations-metrics',
      name: 'Operations Metrics',
      description: 'Operational performance metrics and trends.',
      embedUrl:
        'https://us-east-1.quicksight.aws.amazon.com/sn/embed/share/accounts/861551277616/dashboards/56dc0b03-4e50-4be7-a31c-3562ee933763?directory_alias=vive'
    })
  ];

  ngOnInit(): void {
    this.ActivatedRoute.paramMap.subscribe({
      next: (paramMap) => {
        this.currentDashboardSlug = paramMap.get('dashboardSlug') ?? undefined;
        this.selectedDashboard = this.currentDashboardSlug
          ? this.dashboards.find(
              ({ slug }) => slug === this.currentDashboardSlug
            )
          : undefined;
        this.fullscreenDashboard = undefined;
        // Moving between two dashboards reuses this component, so the new
        // slug arrives with no event to trigger change detection.
        this.ChangeDetectorRef.markForCheck();
      }
    });
  }

  openDashboardPage(dashboard: Dashboard): void {
    this.Router.navigate(['admin', 'dashboards', dashboard.slug]);
  }

  openFullscreen(dashboard: Dashboard): void {
    this.fullscreenDashboard = dashboard;
  }

  closeFullscreen(): void {
    this.fullscreenDashboard = undefined;
  }

  stopCardAction(event: Event): void {
    event.stopPropagation();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.fullscreenDashboard) {
      this.closeFullscreen();
    }
  }

  private createDashboard({
    slug,
    name,
    description,
    embedUrl
  }: Omit<Dashboard, 'src'>): Dashboard {
    return {
      slug,
      name,
      description,
      embedUrl,
      src: this.DomSanitizer.bypassSecurityTrustResourceUrl(embedUrl)
    };
  }
}
